// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import useBlacklistUsers from '../../../hooks/mutations/admin/useBlacklistUsers';
import useMutate from '../../../hooks/mutations/useMutate';

interface DialogProps {
  open: boolean;
  onClose: () => void;
}

export const BlacklistUserDialog = ({ open, onClose }: DialogProps) => {
  const [partyId, setPartyId] = useState('');
  const [reason, setReason] = useState('');
  const blacklistMutation = useBlacklistUsers();
  const mutate = useMutate();

  const handleSubmit = async () => {
    await mutate(
      blacklistMutation,
      { blacklistedUsers: [{ partyId, reason }] },
      'User blacklisted successfully',
      'Failed to blacklist user',
    );
    setPartyId('');
    setReason('');
    onClose();
  };

  const isValid = partyId.trim() !== '' && reason.trim() !== '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Blacklist User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Party ID"
            value={partyId}
            onChange={(e) => setPartyId(e.target.value)}
            placeholder="party::namespace"
            fullWidth
            helperText="Format: alphanumeric::alphanumeric"
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || blacklistMutation.isPending}
        >
          Blacklist
        </Button>
      </DialogActions>
    </Dialog>
  );
};
